/**
 * **A session the trainer is putting on the calendar** — where a new one starts, how long it runs,
 * what stands in its way, and the body `POST /calendarevent` is sent.
 *
 * A draft is a WALL reading on the trainer's clock — a day, minutes past its midnight and a length —
 * because that is what the trainer chose and what the wire carries (`localDate`, `localTime`,
 * `zoneId`). The instant is derived from it here, once, for the overlap and working-hours checks.
 *
 * Its length defaults to the booking policy's for the kind (`trainingMinutes`,
 * `consultationMinutes`; the server's own defaults are 60 and 45, `BookingDefaults`), and a kind
 * switched later takes the new kind's length only while the trainer has not set one of their own.
 *
 * Two things a draft can never be, because the server refuses both whatever else is true: a start
 * at midnight (`midnight_is_not_an_hour` — a 00:00 booking would read as a day with no hour on
 * every build still reading the legacy stamp) and a length outside 5–480 minutes. A third the
 * server would accept and this screen refuses: a start already behind "now" on the trainer's
 * clock — a session booked into the past is a record, not an appointment, and no canon draws it.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { occurrenceKindOf } from '~/entities/calendar-event';
import type {
  TrainWithMeModulesBookingDtosResponsesBookingPolicyResponse as BookingPolicyResponse,
  TrainWithMeModulesTrainingDtosRequestsCreateCalendarEventRequest as CreateCalendarEventRequest,
} from '~/shared/api/generated/model';

import type { BookingSession, SessionKind } from './bookingSession';
import { isOutsideHours, sessionsOverlapping, type TimeSpan } from './busyTime';
import {
  chosenCallLink,
  type CallLinkChoiceView,
  type CallLinkSourceChoice,
  type ChosenCallLink,
} from './callLinkChoice';
import {
  minutesOfTime,
  mondayOfDay,
  paddedClock,
  shiftDay,
  wallReading,
  zonedInstant,
  type CivilDay,
} from './trainerClock';
import type { TrainerBlock, WorkingDay } from './workingHours';

/** The grid's snap and the form's time step: a start or an end lands on a quarter hour. */
export const STEP_MINUTES = 15;

/** The lengths the server accepts for one session (`durationMinutes`, 5–480). */
export const DURATION_BOUNDS = { min: 5, max: 480 } as const;

/** Where a new day's session starts when the day says nothing better — no hours, not today. */
const DEFAULT_DAY_START = 9 * 60;

/** The server's own defaults (`BookingDefaults`), for a policy that has not answered. */
const FALLBACK_POLICY: DraftPolicy = { trainingMinutes: 60, consultationMinutes: 45 };

const MINUTE_MS = 60_000;

/** The two lengths a new session starts from. */
export interface DraftPolicy {
  readonly trainingMinutes: number;
  readonly consultationMinutes: number;
}

export interface SessionDraft {
  readonly clientId: number | null;
  readonly kind: SessionKind;
  readonly day: CivilDay;
  /** Minutes past the day's midnight on the trainer's clock. */
  readonly startMinutes: number;
  readonly durationMinutes: number;
  /** „Rozmowa wideo" — sent only for a consultation (a workout is refused it). */
  readonly videoCall: boolean;
  /** Meet or the trainer's own link; `null` = the panel's default (`model/callLinkChoice`). */
  readonly callSource: CallLinkSourceChoice | null;
  /** The link field; `null` = untouched, so it shows the standing link. */
  readonly callLinkText: string | null;
}

/** A draft that names its client — the only kind the wire takes. */
export type ClientDraft = SessionDraft & { readonly clientId: number };

export function namesClient(draft: SessionDraft): draft is ClientDraft {
  return draft.clientId !== null;
}

/** Why a draft cannot be sent yet. */
export type DraftProblem = 'noClient' | 'midnight' | 'duration' | 'pastDay' | 'past' | 'callLink';

/** The field each problem is about — the one the form marks and takes focus to. */
export type DraftField = 'client' | 'date' | 'time' | 'duration' | 'callLink';

export const DRAFT_PROBLEM_FIELD: Readonly<Record<DraftProblem, DraftField>> = {
  noClient: 'client',
  midnight: 'time',
  // A day already gone is the DATE's fault; a gone hour on today is the time's.
  pastDay: 'date',
  past: 'time',
  duration: 'duration',
  callLink: 'callLink',
};

/** What the form says about a draft before it is sent. */
export interface DraftReview {
  readonly span: TimeSpan;
  /** `null` when the day's hours are not known here. */
  readonly outsideHours: boolean | null;
  /** Sessions already holding part of this time, earliest first. */
  readonly overlaps: readonly BookingSession[];
  /** The first thing standing in the way of sending, or `null`. */
  readonly problem: DraftProblem | null;
  /** Where a video consultation's link comes from; `null` when nothing is chosen here (server default). */
  readonly callLink: ChosenCallLink | null;
}

/** What the draft is checked against: the sessions and hours read for its day, if any. */
export interface DraftContext {
  readonly zone: string;
  readonly nowMs: number;
  /** `null` when the draft's day is not among the days read. */
  readonly sessions: readonly BookingSession[] | null;
  readonly working: WorkingDay | null;
  readonly blocks: readonly TrainerBlock[];
  /** The link choice the panel offers, `null` while the Google connection is read. */
  readonly call?: CallLinkChoiceView | null;
  readonly standingLink?: string | null;
}

/** The policy's two lengths, or the server's defaults while it has not answered. */
export function draftPolicy(policy: BookingPolicyResponse | undefined): DraftPolicy {
  return {
    trainingMinutes: policy?.trainingMinutes ?? FALLBACK_POLICY.trainingMinutes,
    consultationMinutes: policy?.consultationMinutes ?? FALLBACK_POLICY.consultationMinutes,
  };
}

/** The length a new session of `kind` starts with. */
export function defaultMinutes(kind: SessionKind, policy: DraftPolicy): number {
  return kind === 'consultation' ? policy.consultationMinutes : policy.trainingMinutes;
}

/**
 * The next quarter hour after `nowMs` on the trainer's clock — where "+" puts a new session. A
 * quarter that falls on midnight moves one step on, because midnight is not an hour (see header).
 */
export function nextStart(nowMs: number, zone: string): { day: CivilDay; minutes: number } {
  const now = wallReading(nowMs, zone);
  const minutes = (Math.floor(now.minutes / STEP_MINUTES) + 1) * STEP_MINUTES;
  if (minutes < 1440) return { day: now.day, minutes };
  return { day: shiftDay(now.day, 1), minutes: STEP_MINUTES };
}

/** A new draft at a chosen start, with the kind's own length. */
export function draftAt(
  start: { day: CivilDay; minutes: number },
  policy: DraftPolicy,
  kind: SessionKind = 'training',
): SessionDraft {
  return {
    clientId: null,
    kind,
    day: start.day,
    startMinutes: start.minutes,
    durationMinutes: defaultMinutes(kind, policy),
    videoCall: false,
    callSource: null,
    callLinkText: null,
  };
}

/**
 * A new draft from a span dragged across the week grid. A click selects a single quarter; that is
 * a start, not a length, and takes the kind's own length instead. A span starting behind `nowMs`
 * starts nothing: `null`.
 */
export function draftFromSpan(
  span: TimeSpan,
  zone: string,
  policy: DraftPolicy,
  nowMs: number,
): SessionDraft | null {
  // A span dragged out over time already behind now starts nothing (see the header).
  if (span.startMs < nowMs) return null;
  const start = wallReading(span.startMs, zone);
  const minutes = Math.round((span.endMs - span.startMs) / MINUTE_MS);
  const draft = draftAt({ day: start.day, minutes: start.minutes }, policy);
  return minutes <= STEP_MINUTES ? draft : { ...draft, durationMinutes: minutes };
}

/**
 * A new draft for a whole day — a month cell clicked. Today starts at the next quarter; another
 * day at its first working hour, or at nine where it has none. A day already behind today starts
 * nothing: `null`.
 */
export function draftForDay(
  day: CivilDay,
  context: { nowMs: number; zone: string; working: WorkingDay | null; policy: DraftPolicy },
): SessionDraft | null {
  if (day < wallReading(context.nowMs, context.zone).day) return null;
  const next = nextStart(context.nowMs, context.zone);
  if (next.day === day) return draftAt(next, context.policy);
  const opening = context.working?.windows[0]?.start;
  return draftAt(
    { day, minutes: opening !== undefined && opening > 0 ? opening : DEFAULT_DAY_START },
    context.policy,
  );
}

/**
 * The kind switched on a draft or a series. The length follows the kind while it is still the
 * previous kind's default — a length the trainer set themselves is theirs and stays.
 */
export function withKind<
  T extends { readonly kind: SessionKind; readonly durationMinutes: number },
>(value: T, kind: SessionKind, policy: DraftPolicy): T {
  const untouched = value.durationMinutes === defaultMinutes(value.kind, policy);
  return {
    ...value,
    kind,
    durationMinutes: untouched ? defaultMinutes(kind, policy) : value.durationMinutes,
  };
}

/** The draft moved and stretched to a span the grid reports, read on the trainer's clock. */
export function withSpan(draft: SessionDraft, span: TimeSpan, zone: string): SessionDraft {
  const start = wallReading(span.startMs, zone);
  return {
    ...draft,
    day: start.day,
    startMinutes: start.minutes,
    durationMinutes: Math.round((span.endMs - span.startMs) / MINUTE_MS),
  };
}

/** The span of real time a day, a start and a length name on the trainer's clock. */
export function draftSpan(
  draft: Pick<SessionDraft, 'day' | 'startMinutes' | 'durationMinutes'>,
  zone: string,
): TimeSpan {
  const startMs = zonedInstant(draft.day, draft.startMinutes, zone);
  return { startMs, endMs: startMs + draft.durationMinutes * MINUTE_MS };
}

/** Whether a length is one the server accepts. */
export function isBookableLength(minutes: number): boolean {
  return (
    Number.isInteger(minutes) && minutes >= DURATION_BOUNDS.min && minutes <= DURATION_BOUNDS.max
  );
}

/** The form's reading of a draft: where it sits, what it runs into, and what stops it being sent. */
/** A video consultation's link choice as it would be sent; `null` for anything else or an unread choice. */
export function draftCallLink(
  draft: SessionDraft,
  call: CallLinkChoiceView | null,
  standingLink: string | null,
): ChosenCallLink | null {
  if (draft.kind !== 'consultation' || !draft.videoCall || call === null) return null;
  return chosenCallLink(call, draft.callSource ?? call.defaultSource, draft.callLinkText ?? call.prefill, standingLink);
}

const WIRE_SOURCE = { meet: 'Meet', standing: 'Standing', manual: 'Manual', none: 'None' } as const;

export function reviewDraft(draft: SessionDraft, context: DraftContext): DraftReview {
  const span = draftSpan(draft, context.zone);
  const problem: DraftProblem | null =
    draft.clientId === null
      ? 'noClient'
      : draft.startMinutes === 0
        ? 'midnight'
        : !isBookableLength(draft.durationMinutes)
          ? 'duration'
          : draft.day < wallReading(context.nowMs, context.zone).day
            ? 'pastDay'
            : span.startMs < context.nowMs
              ? 'past'
              : null;
  const callLink = draftCallLink(draft, context.call ?? null, context.standingLink ?? null);
  return {
    span,
    outsideHours: isOutsideHours(span, context.working, context.blocks, context.zone),
    overlaps: context.sessions === null ? [] : sessionsOverlapping(context.sessions, span),
    problem: problem ?? (callLink?.kind === 'invalid' ? 'callLink' : null),
    callLink,
  };
}

/** The wire's name for the kind. */
export function wireType(kind: SessionKind): 'Workout' | 'Consultation' {
  return kind === 'consultation' ? 'Consultation' : 'Workout';
}

/**
 * The body of `POST /calendarevent` — the wall reading and the zone its digits were read on,
 * ALWAYS: the grid's zone. Leaving it out would not reach the grid's clock — the server falls back
 * trainer → client → Warsaw, so a trainer with no published zone would book on the CLIENT's clock.
 * `isRemote` is never sent: for a trainer the server decides that itself and ignores the flag.
 * `isVideoCall` goes only with a consultation the trainer marked „Rozmowa wideo", with its link source.
 */
export function createRequest(
  draft: ClientDraft,
  zone: string,
  callLink: ChosenCallLink | null = null,
): CreateCalendarEventRequest {
  return {
    customerId: draft.clientId,
    type: wireType(draft.kind),
    localDate: draft.day,
    localTime: paddedClock(draft.startMinutes),
    durationMinutes: draft.durationMinutes,
    zoneId: zone,
    // Left out for an in-person session, as before the field existed.
    ...(draft.kind === 'consultation' && draft.videoCall ? { isVideoCall: true } : {}),
    // The chosen source, and the link only with Manual (`call_link_source_mismatch` otherwise).
    ...(callLink === null || callLink.kind === 'invalid' || !draft.videoCall || draft.kind !== 'consultation'
      ? {}
      : { callLinkSource: WIRE_SOURCE[callLink.kind], ...(callLink.kind === 'manual' ? { callLink: callLink.link } : {}) }),
  };
}

/** A session just created, drawn where it was composed until a read of its week decides. */
export interface CreatedSession {
  readonly id: number;
  readonly kind: SessionKind;
  readonly span: TimeSpan;
  /** The Monday of its week on the trainer's clock — the read that must show it. */
  readonly week: CivilDay;
  /** How many answers that week's read had given when the write was accepted. */
  readonly readsBefore: number;
}

/** The overlay a created session leaves, from the body that created it and the id it got. */
export function createdFromRequest(
  id: number,
  body: CreateCalendarEventRequest,
  zone: string,
  readsBefore: number,
): CreatedSession {
  const day = body.localDate ?? '';
  const draft = {
    day,
    startMinutes: minutesOfTime(body.localTime) ?? 0,
    durationMinutes: body.durationMinutes ?? 0,
  };
  return {
    id,
    kind: occurrenceKindOf(body.type),
    span: draftSpan(draft, zone),
    week: mondayOfDay(day),
    readsBefore,
  };
}

/** The read of the calendar on screen: its first day, and how many answers it has given. */
export interface CalendarRead {
  readonly first: CivilDay;
  readonly answers: number;
}

/**
 * The created sessions to draw where they were composed: those the read on screen does not show,
 * for as long as that read may simply not know them yet. An answer of the session's own week that
 * came AFTER the write, and does not have it, is newer than the write — the session was moved or
 * called off meanwhile — and the overlay goes. Self-healing: nothing has to remember to clear it,
 * and a failed read somewhere else (a neighbour's prefetch) cannot keep it.
 *
 * Counted in answers, not in clock time: two reads in the same millisecond, or under a clock that
 * stands still, are still told apart.
 */
export function createdNotYetRead(
  created: readonly CreatedSession[],
  shown: readonly { readonly session: { readonly id: number } }[],
  read: CalendarRead,
): CreatedSession[] {
  const ids = new Set(shown.map((entry) => entry.session.id));
  return created.filter(
    (entry) =>
      !ids.has(entry.id) && !(read.first === entry.week && read.answers > entry.readsBefore),
  );
}
